import {
    Box,
    EmptyState,
    FavFilled,
    LinkText,
    Table,
    TextCell,
    Tooltip,
    TooltipContent,
    TooltipTrigger,
    useSubPage,
} from "@bcc-ui/ui-components";
import { useFeature } from "flagged";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { Column } from "react-table";

import ManageRoleForm, { FormValues } from "./manage-role/ManageRoleForm";
import style from "./user-roles.scss";
import { UserRolesKebabMenu } from "./UserRolesKebabMenu";
import { viewActionsContext } from "components/layout/ApplicationLayout";
import { LoadingIndicator } from "components/loading-indicator/LoadingIndicator";
import Modal from "components/modal/Modal";
import SearchView from "components/search/SearchView";
import { TextBlock } from "components/typography/textBlock/TextBlock";
import AuthorityView from "components/users/user-roles/manage-role/AuthorityView";
import { ManageRoleViewSubpage } from "components/users/user-roles/manage-role/ManageRoleViewSubpage";
import { AUTH_ROLE_CREATE, AUTH_ROLE_DEFAULT, AUTH_ROLE_DELETE, AUTH_ROLE_EDIT } from "domain/authority";
import { TABLE_PAGE_LIMIT } from "domain/globalConstants";
import { Role, UserRolesTableData } from "domain/roles";
import { FLAG_RESTRUCTURED_ROLE_MANAGEMENT } from "services/feature/FeatureFlagService";
import { rolesService } from "services/roles/RolesService";
import { Action, Category, usageStatisticsService } from "services/statistics/UsageStatisticsService";
import { userSessionService } from "services/user/UserSessionService";
import buttons from "styles/buttons.scss";
import formStyle from "styles/form.scss";
import layoutStyle from "styles/layout.scss";
import { formatDateWithoutTime } from "utils/format";
import { logger } from "utils/logging";
import { RepositoryKey } from "utils/repository";

import testIds from "testIds.json";

interface Result {
    title: string;
    message: string;
}
interface TableState {
    userRolesData: UserRolesTableData[];
    cursor: string;
    scrollPosition?: number;
}

export interface Props {
    count: number;
    initialRoleData: Role;
    onRoleEdit: () => void;
    search?: string;
    onRoleDelete: () => void;
    refreshCount: number;
}

export const UserRolesTable = (props: Props) => {
    const { t } = useTranslation();
    const tableContainerRef = React.useRef<HTMLDivElement>(null);
    const viewActions = React.useContext(viewActionsContext);
    const { current: abortControllers } = React.useRef<AbortController[]>([]);

    const [tableState, setTableState] = React.useState<TableState>({
        userRolesData: [],
        cursor: "",
        scrollPosition: 0,
    });
    const [requestFailureMessage, setRequestFailureMessage] = React.useState<string>("");
    const [result, setResult] = React.useState<Result>({ title: "", message: "" });
    const [initialLoading, setInitialLoading] = React.useState<boolean>(true);
    const [loading, setLoading] = React.useState<boolean>(false);
    const [search, setSearch] = React.useState("");
    const [editRoleFormVisible, setEditRoleFormVisible] = React.useState(false);
    const [activeRoleDetails, setActiveRoleDetails] = React.useState<Role>(props.initialRoleData);
    const [roleDetailsVisibility, setRoleDetailsVisibility] = React.useState(false);
    const [resultVisible, setResultVisible] = React.useState(false);
    // TODO BCC-7836: remove isRestructuredRoleManagament once the ticket is done
    const isRestructuredRoleManagament = useFeature(FLAG_RESTRUCTURED_ROLE_MANAGEMENT);
    const { setSubPage } = useSubPage();

    const showRoleDetails = (rolesData: Role) => {
        setRoleDetailsVisibility(true);
        setActiveRoleDetails(rolesData);
    };

    const openRoleDetailsSubpage = (rolesData: Role) => {
        setActiveRoleDetails(rolesData);
        setSubPage(
            <ManageRoleViewSubpage
                name={rolesData.name}
                authorities={rolesData.authority}
                targets={rolesData.targets}
                isManager={rolesData.defaultMigrationRole}
                isViewRole={true}
                description=""
            />
        );
    };

    const hideRoleDetails = () => {
        setRoleDetailsVisibility(false);
    };

    const showResult = (resultToShow: Result) => {
        setResult(resultToShow);
        setResultVisible(true);
    };
    const hideResult = () => {
        setResultVisible(false);
        props.onRoleEdit();
    };

    const fetchData = (initialLoading: boolean) => {
        setLoading(true);
        setInitialLoading(initialLoading);
        const abortController = new AbortController();
        abortControllers.push(abortController);
        rolesService
            .fetchRoles(abortController, "", initialLoading ? "" : tableState.cursor, search)
            .then((data) => {
                setTableState((prevState) => ({
                    ...prevState,
                    scrollPosition: prevState.userRolesData.length - 1,
                    userRolesData: prevState.userRolesData.concat(data.roles),
                    cursor: data.cursor,
                }));
                setLoading(false);
                setRequestFailureMessage("");
            })
            .catch(() => {
                if (!abortController?.signal.aborted) {
                    setRequestFailureMessage(t("UserRoles.table.requestFailed"));
                }
            })
            .finally(() => {
                if (!abortController?.signal.aborted) {
                    setLoading(false);
                    setInitialLoading(false);
                }
            });
    };

    const editRoleSubmitEventHandler = async ({
        name,
        description,
        authorities,
        targets,
    }: FormValues): Promise<void> => {
        const abortController = new AbortController();
        abortControllers.push(abortController);
        const { signal } = abortController;
        try {
            await rolesService.editRole(
                activeRoleDetails.roleUuid,
                name,
                description,
                authorities,
                targets,
                abortController
            );
        } catch (e) {
            if (!signal.aborted) {
                setEditRoleFormVisible(false);
                showResult({
                    title: t("ManageRoleForm.edit.failure.title"),
                    message: t("ManageRoleForm.edit.failure.message"),
                });
                logger.error("Failed to edit a role:", e);
            }
            return;
        }
        if (signal.aborted) {
            return;
        }
        setRoleDetailsVisibility(false);
        setEditRoleFormVisible(false);
        showResult({
            title: t("ManageRoleForm.edit.success.title"),
            message: t("ManageRoleForm.edit.success.message"),
        });
    };

    const modal =
        activeRoleDetails != null ? (
            <Modal
                key={1}
                isOpen={roleDetailsVisibility}
                hideModal={hideRoleDetails}
                modalTitle={t("RoleDetails.title", { name: activeRoleDetails.name })}
            >
                <div className={style.fixedWidthModal}>
                    <AuthorityView
                        authorities={activeRoleDetails.authority}
                        targets={activeRoleDetails.targets}
                        onView={true}
                        isManager={activeRoleDetails.defaultMigrationRole}
                    />
                </div>
                <div className={style.buttonContainer}>
                    {userSessionService.userHasAllAuthorities([AUTH_ROLE_EDIT]) && (
                        <button
                            className={buttons.primaryButtonWithoutIcon}
                            data-testid={testIds.workArea.roles.manageRoleDialog.editButton}
                            onClick={() => setEditRoleFormVisible(true)}
                        >
                            {t("Common.edit")}
                        </button>
                    )}
                    <button
                        className={buttons.secondaryButtonWithoutIcon}
                        onClick={() => setRoleDetailsVisibility(false)}
                        data-testid={testIds.common.dialog.closeButton}
                    >
                        {t("Common.close")}
                    </button>
                </div>
            </Modal>
        ) : null;

    const columns: Array<Column<UserRolesTableData>> = [
        {
            Header: () => t("UserRoles.table.roleName"),
            accessor: "name",
            Cell: (cellInfo) => (
                <TextCell>
                    <Box verticalAlignment={"Center"}>
                        {userSessionService.userHasAnyAuthority([
                            AUTH_ROLE_EDIT,
                            AUTH_ROLE_DELETE,
                            AUTH_ROLE_DEFAULT,
                            AUTH_ROLE_CREATE,
                        ]) && (
                            <UserRolesKebabMenu
                                roleUuid={cellInfo.cell.row.original.roleUuid}
                                name={cellInfo.cell.row.original.name}
                                description={cellInfo.cell.row.original.description}
                                authorities={cellInfo.cell.row.original.authorities}
                                targets={cellInfo.cell.row.original.targets}
                                isManager={cellInfo.cell.row.original.defaultMigrationRole}
                                isDefault={cellInfo.cell.row.original.defaultRole}
                                onRoleEdit={props.onRoleEdit}
                                onRoleDelete={props.onRoleDelete}
                            />
                        )}

                        <LinkText
                            as={"button"}
                            onClick={() => {
                                if (isRestructuredRoleManagament) {
                                    openRoleDetailsSubpage({
                                        roleUuid: cellInfo.cell.row.original.roleUuid,
                                        name: cellInfo.cell.row.original.name,
                                        description: cellInfo.cell.row.original.description,
                                        authority: cellInfo.cell.row.original.authorities,
                                        defaultMigrationRole: cellInfo.cell.row.original.defaultMigrationRole,
                                        targets: cellInfo.cell.row.original.targets,
                                    });
                                } else {
                                    showRoleDetails({
                                        roleUuid: cellInfo.cell.row.original.roleUuid,
                                        name: cellInfo.cell.row.original.name,
                                        description: cellInfo.cell.row.original.description,
                                        authority: cellInfo.cell.row.original.authorities,
                                        defaultMigrationRole: cellInfo.cell.row.original.defaultMigrationRole,
                                        targets: cellInfo.cell.row.original.targets,
                                    });
                                }
                                usageStatisticsService.sendEvent({
                                    category: Category.ROLE,
                                    action: Action.VIEW_ROLE_DETAILS,
                                });
                            }}
                        >
                            {cellInfo.value}
                        </LinkText>
                    </Box>
                </TextCell>
            ),
            width: 150,
        },

        {
            Header: () => t("UserRoles.table.description"),
            accessor: "description",
            Cell: ({ cell: { value } }) => <TextCell>{value}</TextCell>,
            width: 170,
        },
        {
            Header: () => t("UserRoles.table.creationDate"),
            accessor: "createdDate",
            Cell: ({ cell: { value } }) => <TextCell>{formatDateWithoutTime(value)}</TextCell>,
            width: 140,
        },
        {
            Header: () => t("UserRoles.table.lastEdited"),
            accessor: "editedDate",
            Cell: ({ cell: { value } }) => <TextCell>{formatDateWithoutTime(value)}</TextCell>,
            width: 150,
        },
        {
            Header: () => t("UserRoles.table.creator"),
            accessor: "creator",
            Cell: ({ cell: { value } }) => <TextCell>{value}</TextCell>,
            width: 140,
        },
        {
            Header: () => t("UserRoles.table.defaultRole"),
            accessor: "defaultRole",
            Cell: ({ cell: { value } }) =>
                value && (
                    <Tooltip placement={"bottom"}>
                        <TooltipTrigger>
                            <FavFilled />
                        </TooltipTrigger>
                        <TooltipContent>{t("AltText.yourDefaultRole")}</TooltipContent>
                    </Tooltip>
                ),
            width: 120,
        },
    ];

    React.useEffect(() => {
        abortControllers.forEach((abortController) => abortController.abort());
        setTableState({ userRolesData: [], cursor: "", scrollPosition: 0 });
        fetchData(true);
        return () => {
            abortControllers.forEach((abortController) => abortController.abort());
        };
    }, [props.count, props.refreshCount, search]);

    let dataCount = null;
    if (tableState.userRolesData.length > 0) {
        dataCount = <div>{t("Common.defaultSearchResultHint", { dataCount: tableState.userRolesData.length })}</div>;
    }

    const editRoleModal = (
        <Modal
            isOpen={editRoleFormVisible}
            hideModal={() => setEditRoleFormVisible(false)}
            modalTitle={t("ManageRoleForm.edit.editRoleTitle")}
        >
            <div className={style.fixedWidthModal}>
                <TextBlock>{t("ManageRoleForm.edit.editRoleSummaryText")}</TextBlock>
                <ManageRoleForm
                    name={activeRoleDetails.name}
                    description={activeRoleDetails.description}
                    authorities={activeRoleDetails.authority}
                    targets={activeRoleDetails.targets}
                    isManager={activeRoleDetails.defaultMigrationRole}
                    isEditing={true}
                    submitEventHandler={editRoleSubmitEventHandler}
                />
            </div>
        </Modal>
    );

    return (
        <>
            <div className={layoutStyle.aboveTable}>
                <div className={layoutStyle.recordCount}>{dataCount}</div>
                <div className={formStyle.search}>
                    <SearchView setSearch={setSearch} searchInProgress={false} />
                </div>
            </div>
            <div className={layoutStyle.tableWrapper} ref={tableContainerRef}>
                <div className={layoutStyle.tableInnerWrapper}>
                    <Table
                        tableIdentity={RepositoryKey.USER_ROLES_TABLE}
                        data={tableState.userRolesData}
                        columns={columns}
                        loaded={!initialLoading}
                        failureMessage={requestFailureMessage}
                        scrollTo={tableState.scrollPosition}
                        emptyStateMessage={
                            <EmptyState text={t("UsersTable.emptyStateMessage")} viewActions={viewActions} />
                        }
                        testIds={{ itself: testIds.workArea.roles.table.itself }}
                    />
                </div>
            </div>
            {tableState.cursor != null &&
                tableState.userRolesData.length >= TABLE_PAGE_LIMIT &&
                tableState.userRolesData.length != 0 &&
                requestFailureMessage === "" &&
                (loading ? (
                    <LoadingIndicator small={true} withTable />
                ) : (
                    <button
                        className={buttons.loadMoreButtonWithoutIcon}
                        onClick={() => {
                            fetchData(false);
                            usageStatisticsService.sendEvent({
                                action: Action.LOAD_MORE,
                                category: Category.ROLE,
                            });
                        }}
                        data-testid={testIds.common.primaryView.table.loadMoreButton}
                    >
                        {t("Common.loadMore")}
                    </button>
                ))}
            {!isRestructuredRoleManagament && modal}
            {editRoleModal}
            <Modal isOpen={resultVisible} hideModal={hideResult} modalTitle={result.title}>
                <div className={style.resultContainer}>{result.message}</div>
                <div className={style.okButtonContainer}>
                    <button
                        className={buttons.primaryOkButton}
                        onClick={hideResult}
                        data-testid={testIds.common.dialog.closeButton}
                    >
                        {t("Common.ok")}
                    </button>
                </div>
            </Modal>
        </>
    );
};
